import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, FileText, BookOpen, Settings, LogOut, Menu, X,
  ChevronRight, Bell, Download, Shield, Briefcase, PhoneCall, Layers, FolderTree, Users, Mail, Send, PanelsTopLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/lib/api';

const AdminLayout = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await adminAPI.getProfile();
        setAdmin(response.data);
      } catch (error) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'Products', icon: Package, path: '/admin/products' },
    { label: 'Categories', icon: FolderTree, path: '/admin/categories' },
    { label: 'RFQ Submissions', icon: FileText, path: '/admin/rfqs' },
    { label: 'Site Content', icon: Settings, path: '/admin/content' },
    { label: 'Resources', icon: Download, path: '/admin/resources' },
    { label: 'Careers', icon: Briefcase, path: '/admin/jobs' },
    { label: 'Callbacks', icon: PhoneCall, path: '/admin/callbacks' },
    { label: 'Customers', icon: Users, path: '/admin/customers' },
    { label: 'Newsletters', icon: Send, path: '/admin/newsletters' },
    { label: 'Newsletter Setup', icon: PanelsTopLeft, path: '/admin/newsletter-studio' },
    { label: 'Email Settings', icon: Mail, path: '/admin/communications' },
    { label: 'Blog Posts', icon: BookOpen, path: '/admin/blog' },
    { label: 'SEO Settings', icon: Settings, path: '/admin/seo' },
    { label: 'CMS', icon: Layers, path: '/admin/cms' },
    { label: 'Security', icon: Shield, path: '/admin/security' },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0047AB] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#0F172A] text-white transform transition-transform duration-300
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0047AB] rounded flex items-center justify-center font-heading font-bold text-xl">
                SL
              </div>
              <div>
                <span className="font-heading font-bold text-lg">Admin Panel</span>
                <p className="text-xs text-slate-400">Singhania International Ltd.</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-sm transition-colors
                  ${isActive(item.path) 
                    ? 'bg-[#0047AB] text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D32F2F] rounded-full flex items-center justify-center font-bold">
                {admin?.name?.charAt(0) || 'A'}
              </div>
              <div>
                <p className="font-medium">{admin?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400">{admin?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm h-16 flex items-center px-6 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 -ml-2 mr-4"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            data-testid="sidebar-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold text-[#0F172A]">
              {navItems.find(item => isActive(item.path))?.label || 'Admin'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#D32F2F] rounded-full"></span>
            </Button>
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm">
                View Site
              </Button>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
